import * as Api from '@ViewModels';
import React from 'react';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useEventLogging } from '../../../../../models/Logging';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useGetSystemJobQuery } from '../../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';

type Params = {
	impersonationContext: Api.IImpersonationContext;
};

const IMAGE_ASSISTANT_NAME = 'ImageGenerationAgent';

interface SystemJobResult extends Api.ISystemJob {
	resultImages: {
		fileAttachmentId: string;
		mimeType: string;
		sourceFileAttachmentId: string;
	}[];
	metadata: {
		failedImages: number;
		totalImages: number;
		successfulImages: number;
	};
	sourceFileAttachmentIds: string[];
}

export const useApplyBrandingToImages = ({ impersonationContext }: Params) => {
	const logger = useEventLogging('BulkRebrandingTab');

	const userSession = useUserSession();
	const [systemJobId, setSystemJobId] = React.useState<string | undefined>(undefined);

	const [conversationId, setConversationId] = React.useState<string | undefined>(undefined);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError(`useImageAssistantConversation-error`, error);
			toast.error('Failed to create conversation');
		},
		onSuccess: data => {
			if (data?.id) {
				setConversationId(data.id);
			}
		},
	});

	React.useEffect(() => {
		// Skip creating a new conversation if we already have one
		if (conversationId) {
			return;
		}

		if (aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName: IMAGE_ASSISTANT_NAME,
				impersonationContext,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const systemJobQuery = useGetSystemJobQuery<SystemJobResult>({
		enabled: Boolean(systemJobId),
		jobId: systemJobId,
		refetchInterval: query => {
			const data = query.state.data;
			if (!systemJobId) {
				return false;
			}

			return !data || data?.status === 'Processing' ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({ attachmentIds }: { attachmentIds: string[] }) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${IMAGE_ASSISTANT_NAME}/conversation/${conversationId}/stream`,
						impersonationContext,
					}),
					'POST',
					{
						_type: 'AIActionRequest',
						action: {
							_type: 'ImageBrandingAction' as Api.ApiModelType,
							sourceFileAttachmentIds: attachmentIds,
						},
					},
					resolve,
					reject
				);
			}),
		onEvent: event => {
			const data = JSON.parse(event.data);

			if (event.event !== 'ToolResponse') {
				return;
			}

			const toolCall = data?.toolCall;

			if (toolCall?.systemJobId) {
				setSystemJobId(toolCall.systemJobId);
			}
		},
	});

	return {
		applyBranding: aiPromptMutation.mutate,
		isGenerating:
			aiPromptMutation.isLoading || systemJobQuery.isLoading || systemJobQuery.data?.status === 'Processing',
		resultImages: systemJobQuery.data?.resultImages,
		error: systemJobQuery.error,
		failedImages: systemJobQuery.data?.metadata?.failedImages || 0,
	};
};
